import { getDocTypeDisplayName } from './docTypeDisplayNames';

/** Smooth-scroll the viewport to the generation result card. */
export function scrollToResultCard() {
  requestAnimationFrame(() => {
    const el = document.querySelector('[data-testid="result-card"]');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

/**
 * Subtitle line for credits on the "document is ready" card.
 * Avoids misleading "0 credits deducted" on re-runs and idempotent charges.
 */
export function getGenerationCreditsSubtitle(job, defaultCredits) {
  if (!job) return null;

  const required = job.credits_required ?? defaultCredits;
  const charged = job.metadata?.credits_charged;
  const isRerun = Boolean(job.metadata?.rerun_of);

  if (job.status === 'preview_ready') {
    return `${required} credits required to unlock (legacy job).`;
  }

  if (typeof charged === 'number' && charged > 0) {
    return `${charged} credits deducted when generated.`;
  }

  if (isRerun && required > 0) {
    return `${required} credits were deducted when this document was first generated.`;
  }

  if (typeof charged === 'number' && charged === 0 && required > 0) {
    return null;
  }

  if (required > 0) {
    return `${required} credits deducted when generated.`;
  }

  return null;
}

/** Short document names for download buttons (not marketing display names). */
const DOWNLOAD_DOC_NAMES = {
  resume: 'Resume',
  cover_letter: 'Cover Letter',
};

/** Primary download button label for a completed document job. */
export function getDocumentDownloadLabel(documentType, jobStatus, defaultCredits) {
  const normalized = String(documentType || '').trim().replace(/-/g, '_');
  const name = DOWNLOAD_DOC_NAMES[normalized] ?? getDocTypeDisplayName(documentType);
  if (jobStatus === 'preview_ready') {
    return `Unlock & Download ${name} — ${defaultCredits} credits`;
  }
  return `Download ${name}`;
}

/** File metadata suffix (filename, size, credits) for the result card. */
export function formatResultCardMeta(job, defaultCredits) {
  if (!job) return '';

  const parts = [];
  if (job.metadata?.filename) parts.push(job.metadata.filename);
  if (job.metadata?.file_size) {
    parts.push(`${(job.metadata.file_size / 1024).toFixed(1)} KB`);
  }

  const creditsLine = getGenerationCreditsSubtitle(job, defaultCredits);
  if (creditsLine) parts.push(creditsLine);

  return parts.join(' · ');
}
